import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export type BugReportCategory = 'Bug' | 'Feature' | 'Hotfix';
export type BugReportStatus = 'Open' | 'In Work' | 'Complete';
export type BugReportLocation = 
  | 'Contracts' 
  | 'Users' 
  | 'Admin' 
  | 'Dashboard' 
  | 'Categories' 
  | 'Settings' 
  | 'Other'
  | 'Profile'
  | 'Contract Types'
  | 'Currencies' 
  | 'Exchange Rates'
  | 'Groups'
  | 'Contacts';

export interface BugReport {
  id: string;
  userId: string;
  title: string;
  description: string;
  category: BugReportCategory;
  location: string;
  status: BugReportStatus;
  createdAt: Date;
  updatedAt: Date;
  hasScreenshots?: boolean;
  screenshots?: string[];
}

export interface BugReportCreateInput {
  title: string;
  description: string;
  category: BugReportCategory;
  location: string;
  screenshots?: File[];
}

export interface BugReportUpdateInput {
  id: string;
  status?: BugReportStatus;
  title?: string;
  description?: string;
  category?: BugReportCategory;
  location?: string;
}

export function useBugReports() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const fetchBugReports = async (): Promise<BugReport[]> => {
    if (!user?.id) {
      return [];
    }

    const { data, error } = await supabase
      .from("bug_reports")
      .select("*")
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error fetching bug reports:", error);
      toast.error(`Error fetching bug reports: ${error.message}`);
      return [];
    }

    // Process the bug reports data
    const processedReports = await Promise.all((data || []).map(async (item) => {
      // Check if this bug report has screenshots
      let screenshots: string[] = [];
      if (item.has_screenshots) {
        try {
          const { data: screenshotsData, error: screenshotsError } = await supabase
            .storage
            .from('bug-screenshots')
            .list(`${item.id}`);
            
          if (!screenshotsError && screenshotsData) {
            screenshots = screenshotsData.map(file => {
              const publicUrlData = supabase
                .storage
                .from('bug-screenshots')
                .getPublicUrl(`${item.id}/${file.name}`);
              return publicUrlData.data.publicUrl;
            });
          }
        } catch (err) {
          console.error("Error fetching screenshots:", err);
        }
      }
      
      return {
        id: item.id,
        userId: item.user_id,
        title: item.title,
        description: item.description,
        category: item.category as BugReportCategory,
        location: item.location,
        status: item.status as BugReportStatus,
        createdAt: new Date(item.created_at),
        updatedAt: new Date(item.updated_at),
        hasScreenshots: item.has_screenshots,
        screenshots
      };
    }));

    return processedReports;
  };

  const uploadScreenshots = async (reportId: string, files: File[]): Promise<boolean> => {
    if (!files.length) return true;
    
    try {
      for (const file of files) {
        const fileName = `${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
        const { error } = await supabase
          .storage
          .from('bug-screenshots')
          .upload(`${reportId}/${fileName}`, file);
          
        if (error) {
          console.error("Error uploading screenshot:", error);
          toast.error(`Error uploading screenshot: ${error.message}`);
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error("Error in uploadScreenshots:", error);
      return false;
    }
  };

  const createBugReport = async (reportData: BugReportCreateInput): Promise<BugReport> => {
    if (!user?.id) {
      throw new Error("You must be logged in to create a bug report");
    }

    const hasScreenshots = reportData.screenshots && reportData.screenshots.length > 0;

    // Create the bug report record
    const { data, error } = await supabase
      .from("bug_reports")
      .insert({
        user_id: user.id,
        title: reportData.title,
        description: reportData.description,
        category: reportData.category,
        location: reportData.location,
        status: 'Open',
        has_screenshots: hasScreenshots
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating bug report:", error);
      toast.error(`Error creating bug report: ${error.message}`);
      throw error;
    }

    let screenshots: string[] = [];
    
    // Upload screenshots if any
    if (hasScreenshots && reportData.screenshots) {
      const uploadSuccess = await uploadScreenshots(data.id, reportData.screenshots);
      
      if (uploadSuccess && reportData.screenshots) {
        const { data: screenshotsData } = await supabase
          .storage
          .from('bug-screenshots')
          .list(`${data.id}`);
          
        if (screenshotsData) {
          screenshots = screenshotsData.map(file => {
            const publicUrlData = supabase
              .storage
              .from('bug-screenshots')
              .getPublicUrl(`${data.id}/${file.name}`);
            return publicUrlData.data.publicUrl;
          });
        }
      }
    }

    toast.success(`Bug report "${reportData.title}" created successfully`);
    
    return {
      id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description,
      category: data.category as BugReportCategory,
      location: data.location,
      status: data.status as BugReportStatus,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      hasScreenshots: data.has_screenshots,
      screenshots
    };
  };

  const updateBugReport = async (updateData: BugReportUpdateInput): Promise<BugReport> => {
    if (!user?.id) {
      throw new Error("You must be logged in to update a bug report");
    }

    const updates: Record<string, any> = {
      updated_at: new Date().toISOString()
    };

    if (updateData.status) updates.status = updateData.status;
    if (updateData.title) updates.title = updateData.title;
    if (updateData.description) updates.description = updateData.description;
    if (updateData.category) updates.category = updateData.category;
    if (updateData.location) updates.location = updateData.location;

    const { data, error } = await supabase
      .from("bug_reports")
      .update(updates)
      .eq("id", updateData.id)
      .select()
      .single();

    if (error) {
      console.error("Error updating bug report:", error);
      toast.error(`Error updating bug report: ${error.message}`);
      throw error;
    }

    // Fetch screenshots if any
    let screenshots: string[] = [];
    if (data.has_screenshots) {
      try {
        const { data: screenshotsData } = await supabase
          .storage
          .from('bug-screenshots')
          .list(`${data.id}`);
          
        if (screenshotsData) {
          screenshots = screenshotsData.map(file => {
            const publicUrlData = supabase
              .storage
              .from('bug-screenshots')
              .getPublicUrl(`${data.id}/${file.name}`);
            return publicUrlData.data.publicUrl;
          });
        }
      } catch (err) {
        console.error("Error fetching screenshots during update:", err);
      }
    }

    toast.success("Bug report updated successfully");
    
    return {
      id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description,
      category: data.category as BugReportCategory,
      location: data.location,
      status: data.status as BugReportStatus,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      hasScreenshots: data.has_screenshots,
      screenshots
    };
  };

  const deleteBugReport = async (id: string): Promise<void> => {
    if (!user?.id) {
      throw new Error("You must be logged in to delete a bug report");
    }

    // Delete associated screenshots first if they exist
    try {
      const { data: report } = await supabase
        .from("bug_reports")
        .select("has_screenshots")
        .eq("id", id)
        .single();
        
      if (report && report.has_screenshots) {
        // List all files in the report's folder
        const { data: files } = await supabase
          .storage
          .from('bug-screenshots')
          .list(`${id}`);
          
        if (files && files.length) {
          // Delete each file
          await supabase
            .storage
            .from('bug-screenshots')
            .remove(files.map(file => `${id}/${file.name}`));
        }
        
        // Try to remove the folder itself (may not be needed depending on Supabase implementation)
        await supabase
          .storage
          .from('bug-screenshots')
          .remove([`${id}`]);
      }
    } catch (err) {
      console.error("Error cleaning up screenshots:", err);
    }

    // Delete the bug report record
    const { error } = await supabase
      .from("bug_reports")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting bug report:", error);
      toast.error(`Error deleting bug report: ${error.message}`);
      throw error;
    }

    toast.success("Bug report deleted successfully");
  };

  // React Query hooks
  const useBugReportsQuery = () => 
    useQuery({
      queryKey: ["bugReports"],
      queryFn: fetchBugReports,
    });

  const useCreateBugReportMutation = () => 
    useMutation({
      mutationFn: createBugReport,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["bugReports"] });
      }
    });

  const useUpdateBugReportMutation = () => 
    useMutation({
      mutationFn: updateBugReport,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["bugReports"] });
      }
    });

  const useDeleteBugReportMutation = () => 
    useMutation({
      mutationFn: deleteBugReport,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["bugReports"] });
      }
    });

  return {
    useBugReportsQuery,
    useCreateBugReportMutation,
    useUpdateBugReportMutation,
    useDeleteBugReportMutation,
  };
}
